import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { CSSRefTransition } from '@utils/react';
import { ErrorMessage } from '../../atoms/Error/Error.js';
import { getTeamsQuery } from '../settings/molecule.my-teams/api.get-teams.query.js';
import { getComponentEntriesQuery } from './api/query.get-component-entries.js';
import { useComponentEntries } from './hook.component-entries.js';
import { content, ENTER_TRANSITION_DURATION_MS, transitionCls } from './page.styles.js';
import { useComponentEntrySelector, componentEntryStore } from './state.js';
import { jsx, Fragment } from 'react/jsx-runtime';
import { TeacherEntriesView } from './organism.teacher/root.js';
import { StudentEntriesView } from './organism.student/root.js';
import { getNotificationsQuery } from '../notifications/api.get-notifications.query.js';

function EntriesPage() {
  const user = useUserIdentityState(state => state.user);
  const timespan = useComponentEntrySelector(state => state.filters.timespan);
  const entries = useComponentEntries(timespan);

  // Ensure that the searchQuery is reset every time the user leaves the
  // ComponentEntries table
  useEffect(() => {
    return () => {
      componentEntryStore.dispatch(componentEntryStore.actions.setQuery(""));
    };
  }, []);
  return /*#__PURE__*/jsx(TransitionGroup, {
    className: content,
    children: (() => {
      if (entries.isError) {
        return /*#__PURE__*/jsx(CSSRefTransition, {
          classNames: transitionCls,
          timeout: ENTER_TRANSITION_DURATION_MS,
          children: ref => /*#__PURE__*/jsx(EntriesErrorMessage, {
            ref: ref
          })
        }, "error");
      }
      switch (user?.role) {
        case "student":
          return /*#__PURE__*/jsx(CSSRefTransition, {
            classNames: transitionCls,
            timeout: ENTER_TRANSITION_DURATION_MS,
            children: ref => /*#__PURE__*/jsx(StudentEntriesView, {
              ref: ref
            })
          }, "student");
        case "teacher":
          return /*#__PURE__*/jsx(CSSRefTransition, {
            classNames: transitionCls,
            timeout: ENTER_TRANSITION_DURATION_MS,
            children: ref => /*#__PURE__*/jsx(TeacherEntriesView, {
              ref: ref
            })
          }, "teacher");
        default:
          return /*#__PURE__*/jsx(Fragment, {});
      }
    })()
  });
}
function EntriesErrorMessage(props) {
  const notifier = useNotifier();
  const client = useQueryClient();
  const user = useUserIdentityState(state => state.user);
  const timespan = useComponentEntrySelector(state => state.filters.timespan);
  const [isLoading, setLoading] = useState(false);
  const handleRetry = async () => {
    if (isLoading) {
      return;
    }
    setLoading(true);
    try {
      await Promise.all([client.fetchQuery(getComponentEntriesQuery(user, timespan)), client.fetchQuery(getNotificationsQuery(user)), client.fetchQuery(getTeamsQuery.queryConfig({
        user
      }))]);
    } catch {
      notifier.notify({
        id: "entries.page.error",
        type: "error",
        body: "Noget gik galt under indlæsningen, prøv venligst igen om lidt."
      });
    } finally {
      setLoading(false);
    }
  };
  return /*#__PURE__*/jsx(ErrorMessage, {
    ref: props.ref,
    onRetry: handleRetry,
    isLoading: isLoading
  });
}

export { EntriesPage };
//# sourceMappingURL=page.js.map
